import { useState, type ReactNode } from 'react';
import { passthrough } from '../primitives/passthrough.js';
import { Icon, type IconName } from '../primitives/Icon.tsx';
import { Button, IconButton } from '../primitives/Button.tsx';
import { Num } from '../primitives/Num.tsx';
import { SectionLabel } from '../primitives/Surface.tsx';
import type { RowDensity } from './DataTable.tsx';

/**
 * The chrome every list screen wears.
 *
 * The control set is not optional and not per-screen: the props below are
 * required, so a list that forgets its view selector or its density control is
 * a type error rather than something a reviewer might notice. Density is
 * capability, not clutter - the simplification in this product is in the model
 * underneath, never in removing a control a rep uses daily.
 */

export interface SavedView {
  id: string;
  name: string;
  pinned: boolean;
  /** A system view cannot be renamed or deleted, only duplicated. */
  system: boolean;
}

export interface FilterChip {
  /** Stable key, so removing one chip does not re-render the rest. */
  key: string;
  /** What the filter is, in the words the person chose. */
  label: string;
  onRemove: () => void;
}

export interface ListAction {
  key: string;
  label: string;
  icon?: IconName;
  onSelect: () => void;
  /** Rendered as unavailable, with the reason, rather than hidden. */
  unavailableReason?: string;
}

/**
 * Every string this component renders.
 *
 * Required, and passed in by the screen. The shared component library does not
 * own product vocabulary: it has no catalogue, so any string written inside it
 * would be English forever, and an Arabic workspace would see a translated
 * page with an English toolbar in the middle of it. The two-locale test pass
 * is what found exactly that.
 *
 * Making the object required means a new control cannot ship without a label
 * in both languages - the compiler asks for it.
 */
export interface ListChromeLabels {
  searchThisList: string;
  new: string;
  import: string;
  moreActions: string;
  refresh: string;
  listSettings: string;
  filters: string;
  removeFilter: (label: string) => string;
  densityComfortable: string;
  densityCompact: string;
  showAsChart: string;
  showAsTable: string;
  inlineEdit: string;
  unsavedChanges: string;
  save: string;
  saveAs: string;
  discard: string;
  clearSelection: string;
  bulkToolbar: string;
  pinView: (name: string) => string;
  unpinView: (name: string) => string;
}

export interface ListChromeProps {
  labels: ListChromeLabels;
  /** Object icon and title: what am I looking at? */
  icon: IconName;
  title: string;

  /*
   * No view selector here.
   *
   * There were two view controls: the ViewBar, which reads and writes saved
   * views, and a selector in this toolbar drawn with a single option called
   * "All" whose onSelect did nothing. ONE component per question - so the
   * selector is gone, and a list either has the ViewBar above it or has no
   * view control at all.
   */
  /** Set when the current view has been changed and not saved. */
  unsaved: { onSave: () => void; onSaveAs: () => void; onDiscard: () => void } | null;

  /** The status line's parts. The count comes from the server, never the page. */
  total: number;
  /** Already formatted by the one formatter. */
  totalLabel: string;
  sortLabel: string;
  filterLabel: string | null;
  updatedLabel: string;

  /** New, Import, and whatever else this object does. */
  /**
   * Optional on purpose.
   *
   * A toolbar that always draws New and Import draws them for a reader who
   * cannot create, and for an object that has no import path yet. A button
   * wired to nothing looks exactly like the ones beside it that work, so the
   * absence of a handler is the absence of the control.
   */
  onNew?: (() => void) | undefined;
  onImport?: (() => void) | undefined;
  objectActions: ListAction[];
  overflowActions: ListAction[];

  /** Search within this list, distinct from global search. */
  search: string;
  onSearchChange: (value: string) => void;

  density: RowDensity;
  onDensityChange: (density: RowDensity) => void;

  onRefresh: () => void;
  refreshing: boolean;

  inlineEdit: boolean;
  onInlineEditChange: (value: boolean) => void;

  view: 'table' | 'chart';
  onViewChange: (view: 'table' | 'chart') => void;

  /**
   * The filter panel and the list-settings panel, where they exist.
   *
   * Both were required props wired to `() => undefined` on every list: a
   * funnel and a sliders icon that opened nothing. Optional now, so a list
   * draws them when it has somewhere for them to go.
   */
  onOpenFilters?: (() => void) | undefined;
  onOpenSettings?: (() => void) | undefined;

  /** Active filters, each removable in one click. */
  filterChips: FilterChip[];

  /** Bulk actions, shown only when something is selected. */
  selectedCount: number;
  selectedLabel: string;
  onClearSelection: () => void;
  bulkActions: ListAction[];

  children: ReactNode;
  footer: ReactNode;
}

export function ListChrome(props: ListChromeProps): JSX.Element {
  const [overflowOpen, setOverflowOpen] = useState(false);

  return (
    <section data-component="ListChrome" className="flex min-h-0 flex-col gap-3">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 text-h3 font-medium text-ink">
          <Icon name={props.icon} decorative className="text-ink-400" />
          {props.title}
        </h1>


        <div className="ms-auto flex flex-wrap items-center gap-2">
          <label className="relative flex items-center">
            <span className="sr-only">{props.labels.searchThisList}</span>
            <Icon
              name="MagnifyingGlass"
              size="inline"
              decorative
              className="pointer-events-none absolute start-3 text-ink-400"
            />
            <input
              type="search"
              value={props.search}
              onChange={(event) => props.onSearchChange(event.target.value)}
              placeholder={props.labels.searchThisList}
              className="h-input w-reading max-w-full rounded-control border border-control bg-surface ps-input pe-input-x text-body text-ink placeholder:text-ink-300"
            />
          </label>

          {props.onNew ? (
            <Button intent="primary" icon="Plus" onClick={props.onNew}>
              {props.labels.new}
            </Button>
          ) : null}
          {props.onImport ? (
            <Button intent="secondary" icon="Upload" onClick={props.onImport}>
              {props.labels.import}
            </Button>
          ) : null}

          {props.objectActions.map((action) => (
            <Button
              key={action.key}
              intent="secondary"
              onClick={action.onSelect}
              disabled={Boolean(action.unavailableReason)}
              title={action.unavailableReason}
              {...(action.icon ? { icon: action.icon } : {})}
            >
              {action.label}
            </Button>
          ))}

          <div className="relative">
            <IconButton
              icon="DotsThreeVertical"
              label={props.labels.moreActions}
              onClick={() => setOverflowOpen((open) => !open)}
              aria-expanded={overflowOpen}
              aria-haspopup="menu"
            />
            {overflowOpen ? (
              <div
                role="menu"
                className="absolute end-0 top-full z-30 mt-1 w-popover rounded-card bg-surface p-1 shadow-popover"
              >
                {props.overflowActions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    role="menuitem"
                    disabled={Boolean(action.unavailableReason)}
                    title={action.unavailableReason}
                    onClick={() => {
                      action.onSelect();
                      setOverflowOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-control px-2 py-2 text-start text-body text-ink-600 hover:bg-sunken hover:text-ink disabled:text-ink-disabled"
                  >
                    {action.icon ? <Icon name={action.icon} size="inline" decorative /> : null}
                    {action.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <FilterChips
          chips={props.filterChips}
          {...(props.onOpenFilters ? { onOpenPanel: props.onOpenFilters } : {})}
          filtersLabel={props.labels.filters}
          removeLabel={props.labels.removeFilter}
        />

        <div className="ms-auto flex items-center gap-1">
          <ViewToggle
            view={props.view}
            onChange={props.onViewChange}
            chartLabel={props.labels.showAsChart}
            tableLabel={props.labels.showAsTable}
          />
          <DensityToggle
            density={props.density}
            onChange={props.onDensityChange}
            comfortableLabel={props.labels.densityComfortable}
            compactLabel={props.labels.densityCompact}
          />
          <label className="flex items-center gap-2 rounded-control px-2 text-meta text-ink-meta">
            <input
              type="checkbox"
              checked={props.inlineEdit}
              onChange={(event) => props.onInlineEditChange(event.target.checked)}
              className="h-icon-inline w-icon-inline rounded-control border-control"
            />
            {props.labels.inlineEdit}
          </label>
          <IconButton
            icon="ArrowCounterClockwise"
            label={props.labels.refresh}
            busy={props.refreshing}
            onClick={props.onRefresh}
          />
          {props.onOpenSettings ? (
            <IconButton
              icon="SlidersHorizontal"
              label={props.labels.listSettings}
              onClick={props.onOpenSettings}
            />
          ) : null}
        </div>
      </div>

      {props.unsaved ? (
        <div className="flex items-center gap-2 rounded-card bg-bronze-tint px-3 py-2 text-dense text-bronze-text">
          <Icon name="Info" size="inline" decorative />
          <span>{props.labels.unsavedChanges}</span>
          <span className="ms-auto flex gap-2">
            <Button size="small" intent="primary" onClick={props.unsaved.onSave}>
              {props.labels.save}
            </Button>
            <Button size="small" intent="secondary" onClick={props.unsaved.onSaveAs}>
              {props.labels.saveAs}
            </Button>
            <Button size="small" intent="ghost" onClick={props.unsaved.onDiscard}>
              {props.labels.discard}
            </Button>
          </span>
        </div>
      ) : null}

      <StatusLine
        totalLabel={props.totalLabel}
        sortLabel={props.sortLabel}
        filterLabel={props.filterLabel}
        updatedLabel={props.updatedLabel}
      />

      {props.selectedCount > 0 ? (
        <BulkActionBar
          selectedLabel={props.selectedLabel}
          onClear={props.onClearSelection}
          actions={props.bulkActions}
          clearLabel={props.labels.clearSelection}
          toolbarLabel={props.labels.bulkToolbar}
        />
      ) : null}

      {props.children}
      {props.footer}
    </section>
  );
}

/**
 * The status line.
 *
 * Four facts, always in the same order: how many, sorted by what, filtered by
 * what, and how fresh. A list that shows rows without saying how many there
 * are in total invites somebody to conclude there are as many as they can see.
 */
export function StatusLine({
  totalLabel,
  sortLabel,
  filterLabel,
  updatedLabel,
  ...rest
}: {
  totalLabel: string;
  sortLabel: string;
  filterLabel: string | null;
  updatedLabel: string;
} & Record<string, unknown>): JSX.Element {
  const parts = [totalLabel, sortLabel, filterLabel, updatedLabel].filter(Boolean) as string[];
  return (
    <p
      {...passthrough(rest)}
      data-component="StatusLine"
      className="flex flex-wrap items-center gap-2 text-meta text-ink-meta"
    >
      {parts.map((part, index) => (
        <span key={part} className="flex items-center gap-2">
          {index > 0 ? <span aria-hidden>·</span> : null}
          <Num>{part}</Num>
        </span>
      ))}
    </p>
  );
}

/**
 * Filter chips.
 *
 * Each removable in one click. A filter panel that has to be opened to remove
 * one condition is a daily friction point, and the panel is still there for
 * building them.
 */
export function FilterChips({
  chips,
  onOpenPanel,
  filtersLabel,
  removeLabel,
  ...rest
}: {
  chips: FilterChip[];
  /** Absent where this list has no filter panel to open. */
  onOpenPanel?: (() => void) | undefined;
  filtersLabel: string;
  removeLabel: (label: string) => string;
} & Record<string, unknown>): JSX.Element {
  return (
    <div {...passthrough(rest)} data-component="FilterChips" className="flex flex-wrap items-center gap-2">
      {/* The button only where a panel opens. Where there is none, the chips
          still render and each still removes itself, which is the half of
          this that people use every day. */}
      {onOpenPanel ? (
        <Button size="small" intent="secondary" icon="Funnel" onClick={onOpenPanel}>
          {filtersLabel}
        </Button>
      ) : null}
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="flex h-chip items-center gap-1 rounded-chip bg-bronze-tint px-chip-x text-meta text-bronze-text"
        >
          {chip.label}
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={removeLabel(chip.label)}
            className="text-bronze-text hover:text-ink"
          >
            <Icon name="X" size="inline" decorative />
          </button>
        </span>
      ))}
    </div>
  );
}

export function DensityToggle({
  density,
  onChange,
  comfortableLabel,
  compactLabel,
  ...rest
}: {
  density: RowDensity;
  onChange: (density: RowDensity) => void;
  comfortableLabel: string;
  compactLabel: string;
} & Record<string, unknown>): JSX.Element {
  return (
    <IconButton
      {...passthrough(rest)}
      data-component="DensityToggle"
      icon={density === 'comfortable' ? 'Rows' : 'ListDashes'}
      label={density === 'comfortable' ? comfortableLabel : compactLabel}
      onClick={() => onChange(density === 'comfortable' ? 'compact' : 'comfortable')}
    />
  );
}

export function ViewToggle({
  view,
  onChange,
  chartLabel,
  tableLabel,
  ...rest
}: {
  view: 'table' | 'chart';
  onChange: (view: 'table' | 'chart') => void;
  chartLabel: string;
  tableLabel: string;
} & Record<string, unknown>): JSX.Element {
  return (
    <IconButton
      {...passthrough(rest)}
      data-component="ViewToggle"
      icon={view === 'table' ? 'ChartBar' : 'Table'}
      label={view === 'table' ? chartLabel : tableLabel}
      onClick={() => onChange(view === 'table' ? 'chart' : 'table')}
    />
  );
}

/**
 * The bulk bar.
 *
 * Names how many are selected, because "act on the selection" without a count
 * is how somebody deletes four hundred records believing they selected four.
 */
export function BulkActionBar({
  selectedLabel,
  onClear,
  actions,
  clearLabel,
  toolbarLabel,
  ...rest
}: {
  selectedLabel: string;
  onClear: () => void;
  actions: ListAction[];
  clearLabel: string;
  toolbarLabel: string;
} & Record<string, unknown>): JSX.Element {
  return (
    <div
      {...passthrough(rest)}
      data-component="BulkActionBar"
      role="toolbar"
      aria-label={toolbarLabel}
      className="flex flex-wrap items-center gap-2 rounded-card bg-sunken px-3 py-2"
    >
      <Num className="text-dense font-medium text-ink">{selectedLabel}</Num>
      {actions.map((action) => (
        <Button
          key={action.key}
          size="small"
          intent="secondary"
          onClick={action.onSelect}
          disabled={Boolean(action.unavailableReason)}
          title={action.unavailableReason}
          {...(action.icon ? { icon: action.icon } : {})}
        >
          {action.label}
        </Button>
      ))}
      <Button size="small" intent="ghost" className="ms-auto" onClick={onClear}>
        {clearLabel}
      </Button>
    </div>
  );
}

/**
 * The pagination footer.
 *
 * Cursor-based, so it offers next and previous rather than page numbers: a
 * page number implies an offset, and an offset over a list somebody else is
 * writing to shows rows twice or not at all.
 */
export function PaginationFooter({
  showingLabel,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  previousLabel,
  nextLabel,
  navigationLabel,
  ...rest
}: {
  showingLabel: string;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  previousLabel: string;
  nextLabel: string;
  navigationLabel: string;
} & Record<string, unknown>): JSX.Element {
  return (
    <nav
      {...passthrough(rest)}
      data-component="PaginationFooter"
      aria-label={navigationLabel}
      className="flex items-center gap-2 border-t border-t-rule pt-3"
    >
      <Num className="text-meta text-ink-meta">{showingLabel}</Num>
      <span className="ms-auto flex gap-2">
        <Button size="small" intent="secondary" icon="CaretLeft" disabled={!hasPrevious} onClick={onPrevious}>
          {previousLabel}
        </Button>
        <Button size="small" intent="secondary" disabled={!hasNext} onClick={onNext}>
          {nextLabel}
        </Button>
      </span>
    </nav>
  );
}

export { SectionLabel };
